import { useState, useEffect } from "react";

export default function CareerPreferences({ onSubmit }) {
  const [section, setSection] = useState(1);
  const totalSections = 5;

  const [answers, setAnswers] = useState({
    personality: {},
    values: {},
    skills: {},
    interests: {},
    workStyle: "",
  });

  // Page mount animation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [section]);

  const handleChange = (sec, key, value) => {
    setAnswers((prev) => ({
      ...prev,
      [sec]: { ...prev[sec], [key]: value },
    }));
  };

  const next = () => setSection((s) => Math.min(s + 1, totalSections));
  const back = () => setSection((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    try {
      console.log("Final Career Preferences (Raw):", answers);
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      if (!userId) {
        alert("❌ You must be logged in to submit your career preferences.");
        return;
      }

      const dto = {
        PersonalityOrientation: answers.personality.workPreference || "",
        PreferenceForRoutine: answers.personality.routinePreference || "",
        DecisionMakingStyle: answers.personality.decisionMaking || "",
        PlanningStyle: answers.personality.planningStyle || "",
        GroupComfort: answers.personality.groupComfort || "",
        WorkValues: answers.values.jobImportance || "",
        MeaningfulWork: answers.values.meaningfulWork || "",
        JobFlexibility: answers.values.jobPredictability || "",
        Relocation: answers.values.relocation || "",
        CompanyPreference: answers.values.companySize || "",
        SkillsTalents: answers.skills.bestAt || [],
        ComputerComfort: answers.skills.computer || "",
        ProblemSolvingPreference: answers.skills.problemSolving || "",
        SpecialTalents: answers.skills.specialTalents || [],
        DetailVsBigPicture: answers.skills.detailType || "",
        LikesBuildingThings: answers.interests.q0 === "Yes",
        LikesScienceMath: answers.interests.q1 === "Yes",
        LikesArtsPerformance: answers.interests.q2 === "Yes",
        LikesHelping: answers.interests.q3 === "Yes",
        LikesLeading: answers.interests.q4 === "Yes",
        LikesOrganizing: answers.interests.q5 === "Yes",
        WorkStyle: answers.workStyle || "",
      };

      console.log("Submitting Part2 DTO Payload:", dto);

      const res = await fetch(
        `http://103.104.48.64:5276/api/questionnaire/part2?userId=${userId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(dto),
        }
      );

      if (!res.ok) throw new Error(await res.text());
      alert("🎯 Career preferences saved! Your AI insights will be generated next.");
      if (onSubmit) onSubmit();
    } catch (err) {
      console.error("❌ Error submitting Part2:", err);
      alert("❌ Submission failed. Please try again later.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F0A24] via-[#1a103b] to-[#2b0e45] text-white p-6 font-['Cantata_One'] relative overflow-hidden">
      {/* --- glowing background accent --- */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_#EB6AB522,_transparent_60%)] pointer-events-none"></div>

      <div className="relative w-full max-w-3xl rounded-3xl bg-white/5 border border-[#EB6AB5]/20 shadow-[0_0_40px_#EB6AB510] p-8 backdrop-blur-md transition-all duration-500 hover:shadow-[0_0_60px_#EB6AB540] animate-fadeIn">
        {/* ---------- Header ---------- */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#EB6AB5] tracking-wide mb-2">
            Career Preference Questionnaire
          </h1>
          <p className="text-[#EB6AB5]/80 text-sm">
            Section {section} of {totalSections}
          </p>
        </div>

        {/* ---------- Progress Bar ---------- */}
        <div className="w-full bg-[#3b2657] rounded-full h-2 mb-10">
          <div
            className="bg-gradient-to-r from-[#EB6AB5] to-[#ff83d1] h-2 rounded-full transition-all duration-700 shadow-[0_0_10px_#EB6AB5]"
            style={{ width: `${(section / totalSections) * 100}%` }}
          ></div>
        </div>

        {/* ---------- Section Content ---------- */}
        <div className="space-y-5">
          {section === 1 && (
            <SectionBlock
              title="🌟 Personality Orientation"
              color="#EB6AB5"
              items={[
                {
                  key: "workPreference",
                  q: "Do you prefer working:",
                  opts: ["With people", "With tasks/objects", "Both equally"],
                },
                {
                  key: "routinePreference",
                  q: "Do you enjoy:",
                  opts: [
                    "Structured routines",
                    "Variety and change",
                    "No preference",
                  ],
                },
                {
                  key: "decisionMaking",
                  q: "When making decisions, you rely more on:",
                  opts: ["Logic and facts", "Feelings and intuition"],
                },
                {
                  key: "planningStyle",
                  q: "Do you usually:",
                  opts: ["Plan ahead carefully", "Adapt as things happen"],
                },
                {
                  key: "groupComfort",
                  q: "In a group, you feel more comfortable:",
                  opts: [
                    "Leading the group",
                    "Supporting and helping",
                    "Working quietly on your own",
                  ],
                },
              ]}
              sectionKey="personality"
              answers={answers}
              handleChange={handleChange}
            />
          )}

          {section === 2 && (
            <SectionBlock
              title="💼 Work Values"
              color="#EB6AB5"
              items={[
                {
                  key: "jobImportance",
                  q: "What matters most in your job?",
                  opts: [
                    "Stability/security",
                    "Growth opportunities",
                    "Good salary",
                    "Work-life balance",
                  ],
                },
                {
                  key: "meaningfulWork",
                  q: "Which type of work feels most meaningful to you?",
                  opts: [
                    "Helping people directly",
                    "Building/repairing things",
                    "Creating/designing",
                    "Managing/organizing",
                  ],
                },
                {
                  key: "jobPredictability",
                  q: "Do you prefer a job that is:",
                  opts: ["Predictable", "Flexible and varied"],
                },
                {
                  key: "relocation",
                  q: "Would you relocate for better opportunities?",
                  opts: ["Yes", "No", "Maybe"],
                },
                {
                  key: "companySize",
                  q: "Do you prefer working:",
                  opts: [
                    "For a large company",
                    "For a small company/startup",
                    "Self-employed/freelance",
                  ],
                },
              ]}
              sectionKey="values"
              answers={answers}
              handleChange={handleChange}
            />
          )}

          {section === 3 && (
            <div className="animate-fadeIn">
              <h2 className="text-2xl font-bold text-[#EB6AB5] mb-4">
                🧠 Skills & Talents
              </h2>
              <QuestionBox title="Which of these are you best at? (choose 2–3)">
                {[
                  "Talking to people / persuading",
                  "Analyzing numbers or data",
                  "Fixing/building things",
                  "Creative arts / music / design",
                  "Physical activities / sports",
                  "Organizing / planning",
                ].map((o) => (
                  <label key={o} className="block">
                    <input
                      type="checkbox"
                      checked={answers.skills.bestAt?.includes?.(o)}
                      onChange={() => {
                        const arr = answers.skills.bestAt || [];
                        const newArr = arr.includes(o)
                          ? arr.filter((x) => x !== o)
                          : [...arr, o];
                        handleChange("skills", "bestAt", newArr);
                      }}
                      className="mr-2 accent-[#EB6AB5]"
                    />
                    {o}
                  </label>
                ))}
              </QuestionBox>

              <QuestionBox title="How comfortable are you with computers?">
                {["Advanced user", "Basic use only", "Not comfortable"].map(
                  (o) => (
                    <label key={o} className="block">
                      <input
                        type="radio"
                        name="computer"
                        checked={answers.skills.computer === o}
                        onChange={() => handleChange("skills", "computer", o)}
                        className="mr-2 accent-[#EB6AB5]"
                      />
                      {o}
                    </label>
                  )
                )}
              </QuestionBox>
            </div>
          )}

          {section === 4 && (
            <SectionBlock
              title="🎨 Interests (Holland Code)"
              color="#EB6AB5"
              items={[
                "Do you enjoy building/fixing things with your hands?",
                "Do you enjoy solving science or math problems?",
                "Do you enjoy writing, painting, or performing?",
                "Do you enjoy teaching, helping, or caring for others?",
                "Do you enjoy leading people, selling ideas, or taking initiative?",
                "Do you enjoy organizing data, files, or records?",
              ].map((q, i) => ({
                key: `q${i}`,
                q,
                opts: ["Yes", "No"],
              }))}
              sectionKey="interests"
              answers={answers}
              handleChange={handleChange}
            />
          )}

          {section === 5 && (
            <div className="animate-fadeIn">
              <h2 className="text-2xl font-bold text-[#EB6AB5] mb-4">
                ⚙️ Work Style
              </h2>
              <QuestionBox title="Do you prefer:">
                {["Working alone", "Working in a team", "Both equally"].map(
                  (o) => (
                    <label key={o} className="block">
                      <input
                        type="radio"
                        name="workStyle"
                        value={o}
                        checked={answers.workStyle === o}
                        onChange={() =>
                          setAnswers((prev) => ({ ...prev, workStyle: o }))
                        }
                        className="mr-2 accent-[#EB6AB5]"
                      />
                      {o}
                    </label>
                  )
                )}
              </QuestionBox>
            </div>
          )}
        </div>

        {/* ---------- Navigation Buttons ---------- */}
        <div className="mt-10 flex justify-between">
          {section > 1 && (
            <button
              onClick={back}
              className="px-6 py-2 rounded-lg bg-[#3b2657] text-white hover:bg-[#523170] transition transform hover:scale-105"
            >
              ← Back
            </button>
          )}
          {section < totalSections ? (
            <button
              onClick={next}
              className="ml-auto px-8 py-2 bg-gradient-to-r from-[#EB6AB5] to-[#ff83d1] text-[#0F0A24] font-semibold rounded-lg hover:shadow-[0_0_20px_#EB6AB5] transform hover:scale-105 transition-all"
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="ml-auto px-8 py-2 bg-gradient-to-r from-[#63ffb0] to-[#00ffa3] text-[#0F0A24] font-semibold rounded-lg hover:shadow-[0_0_20px_#63ffb0] transform hover:scale-105 transition-all"
            >
              🎯 Submit Preferences
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Reusable Components ---------- */

function SectionBlock({ title, color, items, sectionKey, answers, handleChange }) {
  return (
    <div className="animate-fadeIn">
      <h2 className="text-2xl font-bold mb-6" style={{ color }}>
        {title}
      </h2>
      {items.map(({ key, q, opts }) => (
        <QuestionBox key={key} title={q}>
          {opts.map((o) => (
            <label key={o} className="block">
              <input
                type="radio"
                name={key}
                value={o}
                checked={answers[sectionKey][key] === o}
                onChange={() => handleChange(sectionKey, key, o)}
                className="mr-2 accent-[#EB6AB5]"
              />
              {o}
            </label>
          ))}
        </QuestionBox>
      ))}
    </div>
  );
}

function QuestionBox({ title, children }) {
  return (
    <div className="p-5 mb-5 rounded-2xl bg-white/5 border border-[#EB6AB5]/20 hover:bg-white/10 transition-all shadow-[0_0_15px_#EB6AB510]">
      <p className="font-semibold text-[#ffccf5] mb-2">{title}</p>
      {children}
    </div>
  );
}

/* ---------- Tailwind Animations ---------- */
